import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  ArrowLeft, ChartLine, CurrencyEur, Receipt, CalendarBlank,
  IdentificationCard, Car, Buildings,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip,
  CartesianGrid, Legend,
} from "recharts";
import api from "@/lib/api";
import { fmtDate } from "@/lib/fmt";
import ReferenceBadge from "@/components/ReferenceBadge";
import { useAuth } from "@/contexts/AuthContext";

const PRESETS = [
  { id: "today",  label: "Aujourd'hui" },
  { id: "week",   label: "Cette semaine" },
  { id: "month",  label: "Ce mois" },
  { id: "year",   label: "Cette année" },
  { id: "all",    label: "Tout" },
  { id: "custom", label: "Personnalisé" },
];

const eur = (v) => `${Number(v || 0).toFixed(2)} EUR`;
const pct = (v) => `${Number(v || 0).toFixed(1)}%`;

function StatRow({ label, value, accent = "text-white", testid }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-zinc-800/60 last:border-b-0">
      <span className="text-xs text-zinc-400 uppercase tracking-wider">{label}</span>
      <span data-testid={testid} className={`text-sm sm:text-base font-semibold ${accent}`}>{value}</span>
    </div>
  );
}

function SectionCard({ title, totals, accent, icon: Icon, testidPrefix }) {
  return (
    <div data-testid={`${testidPrefix}-section`} className={`bg-[#121212] border border-zinc-800 rounded-lg p-4 sm:p-5 space-y-1`}>
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-2">
          {Icon && <Icon size={18} className={accent} />}
          <h2 className="text-sm sm:text-base font-medium text-white">{title}</h2>
        </div>
        <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border ${accent === "text-emerald-400" ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-300" : "border-sky-500/30 bg-sky-500/10 text-sky-300"}`}>
          {totals.rides_count} courses
        </span>
      </div>
      <StatRow testid={`${testidPrefix}-revenue`}        label="Chiffre d'affaires" value={eur(totals.revenue)}         accent="text-white" />
      <StatRow testid={`${testidPrefix}-driver-pay`}     label="Versé aux chauffeurs" value={eur(totals.driver_payments)} accent="text-zinc-300" />
      <StatRow testid={`${testidPrefix}-commission`}     label="Commission"          value={eur(totals.commission)}      accent={Number(totals.commission) < 0 ? "text-rose-400" : "text-amber-400"} />
      <StatRow testid={`${testidPrefix}-commission-rate`} label="Taux de commission" value={pct(totals.commission_rate_pct)} accent={Number(totals.commission) < 0 ? "text-rose-400" : "text-amber-300"} />
    </div>
  );
}

function CountsCard({ counts }) {
  return (
    <div data-testid="counts-section" className="bg-[#121212] border border-zinc-800 rounded-lg p-4 sm:p-5">
      <div className="flex items-center gap-2 mb-3">
        <ChartLine size={18} className="text-amber-500" weight="fill" />
        <h2 className="text-sm sm:text-base font-medium text-white">Répartition des courses</h2>
        <span className="ml-auto text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border border-zinc-700 bg-zinc-800/60 text-zinc-300">
          {counts.total} total
        </span>
      </div>
      <div className="grid grid-cols-3 gap-2 sm:gap-3">
        <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-md px-3 py-2.5 text-center">
          <p className="text-[10px] uppercase tracking-wider text-emerald-300">Effectuées</p>
          <p data-testid="count-done" className="text-base sm:text-lg font-semibold text-emerald-200">{counts.done}</p>
          <p data-testid="pct-done" className="text-[11px] text-emerald-300/80">{pct(counts.done_pct)}</p>
        </div>
        <div className="bg-rose-500/10 border border-rose-500/30 rounded-md px-3 py-2.5 text-center">
          <p className="text-[10px] uppercase tracking-wider text-rose-300">Annulées</p>
          <p data-testid="count-cancelled" className="text-base sm:text-lg font-semibold text-rose-200">{counts.cancelled}</p>
          <p data-testid="pct-cancelled" className="text-[11px] text-rose-300/80">{pct(counts.cancelled_pct)}</p>
        </div>
        <div className="bg-sky-500/10 border border-sky-500/30 rounded-md px-3 py-2.5 text-center">
          <p className="text-[10px] uppercase tracking-wider text-sky-300">À venir / en cours</p>
          <p data-testid="count-upcoming" className="text-base sm:text-lg font-semibold text-sky-200">{counts.upcoming}</p>
          <p data-testid="pct-upcoming" className="text-[11px] text-sky-300/80">{pct(counts.upcoming_pct)}</p>
        </div>
      </div>
      <p className="text-[10px] text-zinc-500 mt-2 italic">
        Dénominateur : effectuées + annulées + à venir. Les courses archivées sans historique de complétion sont exclues.
      </p>
    </div>
  );
}

function VehicleMixCard({ mix }) {
  const items = [
    { key: "van",     label: "VAN",            color: "sky",     value: mix.van },
    { key: "other",   label: "Autres véhicules", color: "zinc",  value: mix.other },
    { key: "unknown", label: "Non renseigné",  color: "amber",   value: mix.unknown },
  ];
  const colorMap = {
    sky:   "bg-sky-500/10 border-sky-500/30 text-sky-200",
    zinc:  "bg-zinc-500/10 border-zinc-500/30 text-zinc-200",
    amber: "bg-amber-500/10 border-amber-500/30 text-amber-200",
  };
  return (
    <div data-testid="vehicle-mix-section" className="bg-[#121212] border border-zinc-800 rounded-lg p-4 sm:p-5">
      <div className="flex items-center gap-2 mb-3">
        <Car size={18} className="text-sky-400" weight="fill" />
        <h2 className="text-sm sm:text-base font-medium text-white">Répartition par véhicule</h2>
        <span className="ml-auto text-[10px] uppercase tracking-wider text-zinc-500">sur {mix.done_total} courses effectuées</span>
      </div>
      <div className="grid grid-cols-3 gap-2 sm:gap-3">
        {items.map((it) => (
          <div key={it.key} className={`${colorMap[it.color]} border rounded-md px-3 py-2.5 text-center`}>
            <p className="text-[10px] uppercase tracking-wider">{it.label}</p>
            <p data-testid={`vehicle-${it.key}-count`} className="text-base sm:text-lg font-semibold">{it.value.count}</p>
            <p data-testid={`vehicle-${it.key}-pct`} className="text-[11px] opacity-80">{pct(it.value.pct)}</p>
          </div>
        ))}
      </div>
      <p className="text-[10px] text-zinc-500 mt-2 italic">
        VAN = <code>ride_type="van"</code>. Autres véhicules = eco, berline, tesla, moto. Non renseigné = courses sans catégorie identifiée.
      </p>
    </div>
  );
}

function AgenciesCard({ agencies }) {
  if (!agencies || agencies.length === 0) {
    return (
      <div className="bg-[#121212] border border-zinc-800 rounded-lg p-4 sm:p-5 text-center text-zinc-500 text-sm">
        Aucune course effectuée sur cette période.
      </div>
    );
  }
  return (
    <div data-testid="agencies-section" className="bg-[#121212] border border-zinc-800 rounded-lg overflow-hidden">
      <div className="px-4 py-3 border-b border-zinc-800 flex items-center gap-2">
        <Buildings size={18} className="text-indigo-400" weight="fill" />
        <h2 className="text-sm sm:text-base font-medium text-white">Activité par agence</h2>
        <span className="ml-auto text-[10px] uppercase tracking-wider text-zinc-500">{agencies.length} entrées</span>
      </div>
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-[11px] text-zinc-500 uppercase tracking-wider">
            <tr className="border-b border-zinc-800">
              <th className="text-left px-4 py-2.5">Agence / Source</th>
              <th className="text-right px-4 py-2.5">Courses</th>
              <th className="text-right px-4 py-2.5">CA</th>
              <th className="text-right px-4 py-2.5">Chauffeur</th>
              <th className="text-right px-4 py-2.5">Commission</th>
            </tr>
          </thead>
          <tbody>
            {agencies.map((a) => (
              <tr key={a.agency_id || "__direct__"} className="border-b border-zinc-900 hover:bg-zinc-900/40" data-testid={`agency-row-${a.agency_id || "direct"}`}>
                <td className="px-4 py-2.5 text-zinc-200">{a.agency_name}</td>
                <td className="px-4 py-2.5 text-right text-zinc-300">{a.rides_count}</td>
                <td className="px-4 py-2.5 text-right text-white font-medium whitespace-nowrap">{eur(a.revenue)}</td>
                <td className="px-4 py-2.5 text-right text-zinc-400 whitespace-nowrap">{eur(a.driver_payments)}</td>
                <td className={`px-4 py-2.5 text-right whitespace-nowrap font-semibold ${a.commission < 0 ? "text-rose-400" : "text-amber-300"}`}>{eur(a.commission)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="md:hidden divide-y divide-zinc-900">
        {agencies.map((a) => (
          <div key={a.agency_id || "__direct__"} data-testid={`agency-mobile-${a.agency_id || "direct"}`} className="px-3 py-3">
            <div className="flex items-center gap-2 mb-1">
              <p className="text-sm text-zinc-100 truncate">{a.agency_name}</p>
              <span className="ml-auto text-xs text-zinc-500">{a.rides_count} courses</span>
            </div>
            <div className="grid grid-cols-3 gap-2 text-center">
              <div><p className="text-[9px] text-zinc-500 uppercase">CA</p><p className="text-xs font-semibold text-white">{eur(a.revenue)}</p></div>
              <div><p className="text-[9px] text-zinc-500 uppercase">Chauffeur</p><p className="text-xs text-zinc-300">{eur(a.driver_payments)}</p></div>
              <div><p className="text-[9px] text-zinc-500 uppercase">Commission</p><p className={`text-xs font-semibold ${a.commission < 0 ? "text-rose-300" : "text-amber-300"}`}>{eur(a.commission)}</p></div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function EvolutionChart({ series, granularity }) {
  if (!series || series.length === 0) return null;
  const formatBucket = (b) => {
    if (granularity === "hour") return b.slice(-5); // "HH:00"
    if (granularity === "month") return b;          // "YYYY-MM"
    return b.slice(5);                               // "MM-DD"
  };
  return (
    <div data-testid="evolution-section" className="bg-[#121212] border border-zinc-800 rounded-lg p-4 sm:p-5">
      <div className="flex items-center gap-2 mb-3">
        <ChartLine size={18} className="text-amber-400" weight="fill" />
        <h2 className="text-sm sm:text-base font-medium text-white">Évolution</h2>
        <span className="ml-auto text-[10px] uppercase tracking-wider text-zinc-500">
          {granularity === "hour" ? "par heure" : granularity === "month" ? "par mois" : "par jour"}
        </span>
      </div>
      <div className="w-full h-64">
        <ResponsiveContainer>
          <LineChart data={series.map(s => ({ ...s, bucketLabel: formatBucket(s.bucket) }))} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
            <XAxis dataKey="bucketLabel" stroke="#71717a" tick={{ fontSize: 11 }} />
            <YAxis yAxisId="left" stroke="#71717a" tick={{ fontSize: 11 }} />
            <YAxis yAxisId="right" orientation="right" stroke="#71717a" tick={{ fontSize: 11 }} />
            <Tooltip contentStyle={{ backgroundColor: "#0a0a0a", border: "1px solid #27272a", borderRadius: 6, fontSize: 12 }} labelStyle={{ color: "#a1a1aa" }} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Line yAxisId="left"  type="monotone" dataKey="rides_count" name="Courses" stroke="#38bdf8" strokeWidth={2} dot={false} />
            <Line yAxisId="right" type="monotone" dataKey="commission"  name="Commission" stroke="#fbbf24" strokeWidth={2} dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export default function AdminBusinessDashboard() {
  const navigate = useNavigate();
  const { linkedDriver } = useAuth();
  const [period, setPeriod] = useState("month");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const params = useMemo(() => {
    const p = { period };
    if (period === "custom") {
      if (!start || !end) return null;
      p.start = start;
      p.end = end;
    }
    if (linkedDriver?.id) p.self_driver_id = linkedDriver.id;
    return p;
  }, [period, start, end, linkedDriver]);

  useEffect(() => {
    if (!params) return;
    setLoading(true);
    api.get("/admin/business-dashboard", { params })
      .then(({ data: d }) => setData(d))
      .catch(e => toast.error(e?.response?.data?.detail || "Échec du chargement"))
      .finally(() => setLoading(false));
  }, [params]);

  const totals = data ? data : {
    realized: { revenue: 0, driver_payments: 0, commission: 0, commission_rate_pct: 0, rides_count: 0 },
    forecast: { revenue: 0, driver_payments: 0, commission: 0, commission_rate_pct: 0, rides_count: 0 },
    counts:   { done: 0, cancelled: 0, upcoming: 0, total: 0, done_pct: 0, cancelled_pct: 0, upcoming_pct: 0 },
    vehicle_mix: { done_total: 0, van: { count: 0, pct: 0 }, other: { count: 0, pct: 0 }, unknown: { count: 0, pct: 0 } },
    agencies: [],
    timeseries: [],
    timeseries_granularity: "day",
    rides: [],
  };

  const realizedRows = (totals.rides || []).filter(r => r.section === "realized");
  const forecastRows = (totals.rides || []).filter(r => r.section === "forecast");

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-[#050505]/80 border-b border-zinc-800/50">
        <div className="max-w-6xl mx-auto px-3 sm:px-6 h-14 flex items-center gap-3">
          <Button data-testid="business-back-btn" variant="ghost" size="sm" onClick={() => navigate("/admin")} className="text-zinc-400 hover:text-white hover:bg-zinc-800 px-2">
            <ArrowLeft size={18} />
          </Button>
          <h1 className="text-base sm:text-lg font-medium tracking-tight text-white flex items-center gap-2 min-w-0">
            <ChartLine size={20} className="text-amber-500 shrink-0" weight="fill" />
            <span className="truncate">Business Dashboard</span>
          </h1>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-3 sm:px-6 py-5 space-y-5">
        {/* Filter bar */}
        <div className="bg-[#0e0e0e] border border-zinc-800 rounded-lg p-3 sm:p-4 space-y-3">
          <Tabs value={period} onValueChange={setPeriod}>
            <TabsList data-testid="business-period-tabs" className="bg-[#1A1A1A] border border-zinc-800 grid grid-cols-3 sm:grid-cols-6 gap-1 h-auto p-1">
              {PRESETS.map(p => (
                <TabsTrigger
                  key={p.id}
                  value={p.id}
                  data-testid={`business-period-${p.id}`}
                  className="text-xs sm:text-sm data-[state=active]:bg-amber-500 data-[state=active]:text-black h-9"
                >
                  {p.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
          {period === "custom" && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-zinc-500 text-[11px] uppercase tracking-wider">Du</Label>
                <Input data-testid="business-custom-start" type="date" value={start} onChange={(e) => setStart(e.target.value)} className="bg-[#1A1A1A] border-zinc-700 text-white focus:border-amber-500 h-10 mt-1" />
              </div>
              <div>
                <Label className="text-zinc-500 text-[11px] uppercase tracking-wider">Au</Label>
                <Input data-testid="business-custom-end" type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="bg-[#1A1A1A] border-zinc-700 text-white focus:border-amber-500 h-10 mt-1" />
              </div>
            </div>
          )}
          {data && (
            <p className="text-[11px] text-zinc-500 flex items-center gap-1.5">
              <CalendarBlank size={12} /> Période : <span className="text-zinc-300">{fmtDate(data.start)} → {fmtDate(data.end)}</span>
              {loading && <span className="ml-2 inline-block w-3 h-3 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />}
            </p>
          )}
        </div>

        {/* Counts strip */}
        <CountsCard counts={totals.counts} />

        {/* Finance: Réalisé / Prévisionnel */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4">
          <SectionCard title="Réalisé" totals={totals.realized} accent="text-emerald-400" icon={CurrencyEur} testidPrefix="realized" />
          <SectionCard title="Prévisionnel" totals={totals.forecast} accent="text-sky-400" icon={Receipt} testidPrefix="forecast" />
        </div>

        {/* Vehicle mix */}
        <VehicleMixCard mix={totals.vehicle_mix} />

        {/* Agencies */}
        <AgenciesCard agencies={totals.agencies} />

        {/* Evolution chart */}
        <EvolutionChart series={totals.timeseries} granularity={totals.timeseries_granularity} />

        {/* Self-driver — unchanged */}
        {linkedDriver?.id && data?.self_driver && (
          <div data-testid="self-driver-section" className="bg-[#121212] border border-amber-500/30 rounded-lg p-4 sm:p-5 space-y-1">
            <div className="flex items-center justify-between gap-2 mb-2">
              <div className="flex items-center gap-2">
                <IdentificationCard size={18} className="text-amber-400" weight="fill" />
                <h2 className="text-sm sm:text-base font-medium text-white">Mon activité chauffeur</h2>
                {linkedDriver?.name && <span className="text-xs text-zinc-500">· {linkedDriver.name}</span>}
              </div>
              <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border border-amber-500/30 bg-amber-500/10 text-amber-300">
                {data.self_driver.rides_count} courses
              </span>
            </div>
            <StatRow testid="self-driver-rides-count" label="Courses effectuées" value={`${data.self_driver.rides_completed} / ${data.self_driver.rides_count}`} accent="text-zinc-300" />
            <StatRow testid="self-driver-revenue"     label="Chiffre d'affaires client" value={eur(data.self_driver.revenue)} accent="text-white" />
            <StatRow testid="self-driver-earned"      label="Encaissé en tant que chauffeur" value={eur(data.self_driver.earned)} accent="text-amber-400" />
            <p className="text-[10px] text-zinc-500 pt-2 italic">
              Les courses que vous conduisez vous-même : la totalité du prix client vous revient (aucun versement chauffeur externe).
            </p>
          </div>
        )}

        {!linkedDriver?.id && (
          <div className="text-[11px] text-zinc-500 italic px-1">
            Liez un compte chauffeur (depuis le tableau de bord admin) pour voir ici votre activité personnelle de chauffeur.
          </div>
        )}

        {/* Detail table (unchanged) */}
        {(realizedRows.length + forecastRows.length) > 0 && (
          <div data-testid="business-rides-table" className="bg-[#121212] border border-zinc-800 rounded-lg overflow-hidden">
            <div className="px-4 py-3 border-b border-zinc-800 flex items-center justify-between">
              <h3 className="text-sm font-medium text-white">Détail des courses</h3>
              <span className="text-[10px] text-zinc-500 uppercase tracking-wider">{realizedRows.length + forecastRows.length} courses</span>
            </div>
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-[11px] text-zinc-500 uppercase tracking-wider">
                  <tr className="border-b border-zinc-800">
                    <th className="text-left px-4 py-2.5">Section</th>
                    <th className="text-left px-4 py-2.5">Date</th>
                    <th className="text-left px-4 py-2.5">Référence</th>
                    <th className="text-left px-4 py-2.5">Trajet</th>
                    <th className="text-right px-4 py-2.5">Prix client</th>
                    <th className="text-right px-4 py-2.5">Chauffeur</th>
                    <th className="text-right px-4 py-2.5">Commission</th>
                  </tr>
                </thead>
                <tbody>
                  {[...realizedRows, ...forecastRows].map(r => (
                    <tr key={r.id} className="border-b border-zinc-900 hover:bg-zinc-900/40" data-testid={`business-row-${r.id}`}>
                      <td className="px-4 py-2.5">
                        <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border ${r.section === "realized" ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-300" : "border-sky-500/30 bg-sky-500/10 text-sky-300"}`}>
                          {r.section === "realized" ? "Réalisé" : "Prévu"}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-zinc-300 whitespace-nowrap">{fmtDate(r.pickup_date)} {r.pickup_time}</td>
                      <td className="px-4 py-2.5">{r.reference ? <ReferenceBadge value={r.reference} size="sm" testid={`bd-ref-${r.id}`} /> : <span className="text-zinc-600 text-xs">—</span>}</td>
                      <td className="px-4 py-2.5 text-zinc-300 max-w-[260px] truncate">{r.route_label}</td>
                      <td className="px-4 py-2.5 text-right text-white font-medium whitespace-nowrap">{eur(r.client_price)}</td>
                      <td className="px-4 py-2.5 text-right text-zinc-300 whitespace-nowrap">{eur(r.driver_amount)}</td>
                      <td className="px-4 py-2.5 text-right text-amber-300 font-semibold whitespace-nowrap">{eur(r.commission)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="md:hidden divide-y divide-zinc-900">
              {[...realizedRows, ...forecastRows].map(r => (
                <div key={r.id} data-testid={`business-mobile-row-${r.id}`} className="px-3 py-3">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border ${r.section === "realized" ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-300" : "border-sky-500/30 bg-sky-500/10 text-sky-300"}`}>
                      {r.section === "realized" ? "Réalisé" : "Prévu"}
                    </span>
                    {r.reference && <ReferenceBadge value={r.reference} size="sm" />}
                    <span className="ml-auto text-xs text-zinc-500">{fmtDate(r.pickup_date)}</span>
                  </div>
                  <p className="text-sm text-zinc-200 truncate">{r.route_label}</p>
                  <div className="grid grid-cols-3 gap-2 mt-2 text-center">
                    <div><p className="text-[9px] text-zinc-500 uppercase">Client</p><p className="text-xs font-semibold text-white">{eur(r.client_price)}</p></div>
                    <div><p className="text-[9px] text-zinc-500 uppercase">Chauffeur</p><p className="text-xs text-zinc-300">{eur(r.driver_amount)}</p></div>
                    <div><p className="text-[9px] text-zinc-500 uppercase">Commission</p><p className="text-xs font-semibold text-amber-300">{eur(r.commission)}</p></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {data && (realizedRows.length + forecastRows.length) === 0 && (
          <div className="text-center py-10 text-zinc-500 text-sm">Aucune course sur cette période.</div>
        )}
      </main>
    </div>
  );
}
